import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AmbiguityTypes,
  AnalysisDocumentTypes,
  ClauseScannerAnalysisType,
  DocumentTypes,
  EvaluationTypeLabelsEnum,
  InconsistencyTypes,
  StandardDeviationsTypes,
  SummarizationNamespaces,
  queryKeys,
} from '@/app/utils/constants';
import useEntityDocuments from '@/app/hooks/useEntityDocuments';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  ContractsIcon,
  SettingsIcon,
  NewOfficeIcon,
  FolderIcon,
  Clock04Icon,
  TimeQuarterPassIcon,
} from '@hugeicons/core-free-icons';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  BreadcrumbMenu,
} from '@/app/ui/components/breadcrumb';
import {
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderSidechatButton,
  Header,
  HeaderMobileChatButton,
} from '@/app/ui/components/header';
import { useTranslations } from 'use-intl';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import {
  MainContainer,
  MainFrames,
  MainHeader,
  MainNav,
  MainNavButton,
  MainNavItem,
  MainNavLink,
  MainNavList,
  MainScrollArea,
} from '@/app/ui/components/main';
import { H1, H2 } from '@/app/ui/components/heading';
import AnalysisAssessment from '@/app/components/features/evaluations/analysis/NewAssesment/AnalysisAssessment';
import { StandardDeviationsFrame } from '@/app/components/features/evaluations/frames/StandardDeviationsFrame';
import { ConflictsFrame } from '@/app/components/features/evaluations/frames/ConflictsFrame';
import { ClausesFrame } from '@/app/components/features/evaluations/frames/ClausesFrame';
import { SummaryFrame } from '@/app/components/features/evaluations/frames/SummaryFrame';
import { ExtractionsFrame } from '@/app/components/features/evaluations/frames/ExtractionsFrame';
import { AmbiguitiesFrame } from '@/app/components/features/evaluations/analysis/Ambiguities/AmbiguitiesFrame';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import { WorkPackagesFrame } from '@/app/components/features/evaluations/frames/WorkPackagesFrame';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { OrganizationAvatar } from '@/app/ui/components/organization-avatar';
import { AppContext } from '@/app/appContext';
import { use, useEffect } from 'react';
import { useSuspenseQuery, useQuery } from '@tanstack/react-query';
import { getLatestEvaluationVersion } from '@/app/services/versioningService';
import { useAtom, useSetAtom } from 'jotai';
import { versionPanelOpenAtom } from '@/app/components/features/evaluations/VersionHistoryPanel/store';
import { sidechatOpenAtom } from '@/app/components/shared/Sidechat/store';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.contractHome');
  const tGeneral = useTranslations('general');
  const navigate = useNavigate();
  const { userMeta } = use(AppContext);
  const organizationName = userMeta?.organizationName || '';
  const { contractId, projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));

  const { data: documents } = useEntityDocuments(contractId, DocumentTypes.CONTRACT);

  const { data: latestVersion } = useQuery({
    queryKey: [queryKeys.EVALUATION_LATEST_VERSION, contractId],
    queryFn: async () => (await getLatestEvaluationVersion(contractId)).data,
  });

  const [versionPanelOpen, setVersionPanelOpen] = useAtom(versionPanelOpenAtom);
  const setSidechatOpen = useSetAtom(sidechatOpenAtom);

  useEffect(() => () => setVersionPanelOpen(false), [setVersionPanelOpen]);

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu />
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                <span className="line-clamp-1">{project.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={ContractsIcon} />
                {t('title')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderMobileChatButton />
      </Header>
      <MainScrollArea>
        <DocumentAlerts
          resourceId={contractId}
          summaryType={SummarizationNamespaces.CONTRACT}
          onGoToFiles={() =>
            navigate({
              to: '/project/$projectId/contracts/$contractId/files',
              params: { projectId, contractId },
            })
          }
        />
        <MainHeader>
          <H1>{t('title')}</H1>
        </MainHeader>
        <MainNav>
          <MainNavList>
            {evaluation.companyName && (
              <MainNavItem className="text-muted items-center whitespace-nowrap px-1.75 h-7 gap-1.5 flex text-sm">
                {organizationName === evaluation.companyName ? (
                  <OrganizationAvatar
                    organization={{ id: userMeta.organizationId ?? '', name: organizationName }}
                    size={24}
                    className="rounded-[calc(var(--radius-md)-0.125rem)]"
                  />
                ) : (
                  <HugeiconsIcon icon={NewOfficeIcon} />
                )}
                {evaluation.companyName}{' '}
                {organizationName === evaluation.companyName ? `(${tGeneral('yourOrg')})` : null}
              </MainNavItem>
            )}
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/contracts/$contractId/files"
                params={{ projectId, contractId }}
              >
                <HugeiconsIcon icon={FolderIcon} />
                {t('nav.files')} {!documents?.length ? null : `(${documents.length})`}
              </MainNavLink>
            </MainNavItem>
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/contracts/$contractId/activity"
                params={{ projectId, contractId }}
              >
                <HugeiconsIcon icon={Clock04Icon} />
                {t('nav.activityLog')}
              </MainNavLink>
            </MainNavItem>
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/contracts/$contractId/settings"
                params={{ projectId, contractId }}
              >
                <HugeiconsIcon icon={SettingsIcon} />
                {t('nav.settings')}
              </MainNavLink>
            </MainNavItem>
            {latestVersion && (
              <MainNavItem>
                <MainNavButton
                  data-selected={versionPanelOpen ? '' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    setVersionPanelOpen((prev) => !prev);
                    if (!versionPanelOpen) setSidechatOpen(false);
                  }}
                >
                  <HugeiconsIcon icon={TimeQuarterPassIcon} />
                  {t('nav.versionHistory')} (v{latestVersion.version.versionNumber}{' '}
                  {latestVersion.version.status === 'active' ? tGeneral('latest') : ''})
                </MainNavButton>
              </MainNavItem>
            )}
          </MainNavList>
        </MainNav>
        <MainContainer>
          <H2 className="mb-3">{t('section1Title')}</H2>
          <AnalysisAssessment documentType={AnalysisDocumentTypes.CONTRACT} id={contractId} />
          <H2 className="mb-3 mt-5">{t('section2Title')}</H2>
          <MainFrames>
            <SummaryFrame
              to="/project/$projectId/contracts/$contractId/summary"
              params={{ projectId, contractId }}
              evaluationId={contractId}
              summaryType={SummarizationNamespaces.CONTRACT}
            />
            <WorkPackagesFrame
              type={'Contract'}
              evaluationId={contractId}
              evalLanguage={evaluation.language}
            />
            <ExtractionsFrame
              to="/project/$projectId/contracts/$contractId/extractions"
              params={{ projectId, contractId }}
              evaluationId={contractId}
              summaryType={SummarizationNamespaces.CONTRACT}
              evalLanguage={evaluation.language}
            />
          </MainFrames>
          <H2 className="mb-3 mt-5">{t('section3Title')}</H2>
          <MainFrames>
            <StandardDeviationsFrame
              evaluationId={contractId}
              evalLanguage={evaluation.language}
              deviationsType={StandardDeviationsTypes.CONTRACT}
              to="/project/$projectId/contracts/$contractId/standard-deviations"
              params={{ projectId, contractId }}
            />
            <ConflictsFrame
              evaluationId={contractId}
              inconsistencyType={InconsistencyTypes.CONTRACT}
              to="/project/$projectId/contracts/$contractId/conflicts"
              params={{ projectId, contractId }}
            />
            <AmbiguitiesFrame
              ambiguityType={AmbiguityTypes.CONTRACT}
              id={contractId}
              to="/project/$projectId/contracts/$contractId/ambiguities"
              params={{ projectId, contractId }}
            />
            <ClausesFrame
              evaluationId={contractId}
              clauseScannerType={ClauseScannerAnalysisType.CONTRACT}
              to="/project/$projectId/contracts/$contractId/clauses"
              params={{ projectId, contractId }}
            />
          </MainFrames>
        </MainContainer>
      </MainScrollArea>
    </>
  );
}
